import React, { useEffect, useMemo, useRef } from 'react';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger';
import { PRIORITIES_TABLE_REORDER_DROPDOWN_SELECTOR } from '../../../../common/constants';
import type { Priority, ReorderHandler } from '../../../../common/types';
import { priorityModalSwitcherEntryPoint } from '../../../priority-modals/entrypoint';
import type { PriorityModalProps } from '../../../priority-modals/tmp-priority-modal-switcher';
import { messages } from './messages';

type Props = {
	priority: Priority;
	canMoveUp: boolean;
	canMoveDown: boolean;
	shouldFocus: boolean;
	onReorder: ReorderHandler;
	onSelectPosition: (priority: Priority) => void;
	onClosePriorityModal?: () => void;
	onDeleteConfirm?: (priority: Priority) => void;
	onSetPriorityPosition?: (priorityId: string, order: number) => Promise<void>;
	totalItems?: number;
};

export const Reorder = ({
	priority,
	canMoveUp,
	canMoveDown,
	shouldFocus,
	onReorder,
	onSelectPosition,
	onClosePriorityModal,
	onDeleteConfirm,
	onSetPriorityPosition,
	totalItems,
}: Props) => {
	const { formatMessage } = useIntl();
	const buttonRef = useRef<HTMLButtonElement | null>(null);

	useEffect(() => {
		if (shouldFocus && buttonRef.current) {
			buttonRef.current.focus();
		}
	}, [buttonRef, shouldFocus]);

	const entryPointProps = useMemo(
		() => ({
			onClose: () => onClosePriorityModal && onClosePriorityModal(),
			currentModal: {
				modalName: 'MOVE' as const,
				priority,
			},
			totalItems,
			onDelete: (itemPriority: Priority) => onDeleteConfirm && onDeleteConfirm(itemPriority),
			onSetPosition: (priorityId: string, order: number) =>
				onSetPriorityPosition && onSetPriorityPosition(priorityId, order),
		}),
		[totalItems, onClosePriorityModal, onSetPriorityPosition, onDeleteConfirm, priority],
	);

	if (!canMoveUp && !canMoveDown) {
		return (
			<IconButton
				icon={MoreIcon}
				appearance="subtle"
				label={formatMessage(messages.moveButtonLabel, { priorityName: priority.name })}
				isDisabled
			/>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...props }) => (
				<IconButton
					{...props}
					icon={MoreIcon}
					appearance="subtle"
					label={formatMessage(messages.moveButtonLabel, { priorityName: priority.name })}
					ref={(value) => {
						// We need access to buttonRef for focus management and triggerRef is needed by the dropdown API
						buttonRef.current = value;
						if (typeof triggerRef === 'function') {
							triggerRef(value);
						}
					}}
				/>
			)}
			placement="bottom-start"
			spacing="compact"
			testId={PRIORITIES_TABLE_REORDER_DROPDOWN_SELECTOR}
			shouldRenderToParent
		>
			<DropdownItemGroup>
				{canMoveUp && (
					<DropdownItem onClick={() => onReorder({ priorityId: priority.id, action: 'UP' })}>
						{formatMessage(messages.moveUp)}
					</DropdownItem>
				)}
				{canMoveDown && (
					<DropdownItem onClick={() => onReorder({ priorityId: priority.id, action: 'DOWN' })}>
						{formatMessage(messages.moveDown)}
					</DropdownItem>
				)}
				{fg('jsw_priority_modals_admin_page') ? (
					<ModalEntryPointPressableTrigger
						entryPoint={priorityModalSwitcherEntryPoint}
						// these assertions will be removed in tidying of jsw_priority_modals_admin_page

						entryPointProps={
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							entryPointProps as
								| (Omit<PriorityModalProps, 'onClose'> & {
										onClose?: (() => void) | undefined;
								  })
								| undefined
						}
						interactionName="priorities-table-delete-modal"
						// Todo add below in cleanup of jsw_priority_modals_admin_page
						// modalProps={modalProps}
						// useInternalModal
					>
						{({ ref }) => (
							<DropdownItem
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								ref={ref as React.Ref<HTMLButtonElement>}
								onClick={() => onSelectPosition(priority)}
								testId="admin-pages-priorities.ui.priorities-table.actions.reorder.select-postion"
							>
								{formatMessage(messages.selectPosition)}
							</DropdownItem>
						)}
					</ModalEntryPointPressableTrigger>
				) : (
					<DropdownItem onClick={() => onSelectPosition(priority)}>
						{formatMessage(messages.selectPosition)}
					</DropdownItem>
				)}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
